import {
  CropResidueManagementType,
  STAGE_4,
  SoilMoisture,
  SoilPh,
  SoilTexture,
} from 'constants/FarmboxConstants/stage4.constants';
import { FieldType } from 'enums/FieldTypeEnums';
import styles from './farmFieldFlow.module.scss';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import { CoProductsComponentProps } from 'views/ActivityDataEntry/FarmCommodity/CoProductsComponent';
import React from 'react';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';

interface cropResidueFieldProps extends CoProductsComponentProps {
  cropResidueLength?: number;
  fetchData: (params?: { 'input-field': string }) => void;
  isDisabled?: boolean;
}
interface getCropResidueInitialFieldProps {
  fetchData: (params?: { 'input-field': string }) => void;
  isDisabled?: boolean;
}

interface getSoilMatterFieldProps extends getCropResidueInitialFieldProps {
  debouncedSoilCalculation: (value: number) => void;
}

const {
  CROP_RESIDUE_LABELS,
  SOIL_ORGANIC_LABELS,
  FIELD_NAMES,
  HEADING,
  PLACEHOLDER,
} = STAGE_4;

const { TOTAL_CROP, RESIDUE_TYPE, TOTAL_CROP_PERCENTAGE } = CROP_RESIDUE_LABELS;

const {
  SOIL_TEXTURE,
  SOIL_MOISTURE,
  SOIL_PH,
  SOIL_ORGANIC_PERCENTAGE,
  SOIL_CARBON_PERCENTAGE,
} = SOIL_ORGANIC_LABELS;

const {
  TOTAL_CROP_RESIDUE_FIELD,
  RESIDUE_TYPE_FIELD,
  TOTAL_CROP_PERCENTAGE_FIELD,
  SOIL_TEXTURE_FIELD,
  SOIL_MOISTURE_FIELD,
  SOIL_PH_FIELD,
  SOIL_ORGANIC_PERCENTAGE_FIELD,
  SOIL_CARBON_PERCENTAGE_FIELD,
  SOIL_FIELD,
  CROP_RESIDUE_FIELD,
} = FIELD_NAMES;

const {
  COMMODITY,
  COMMODITY_CROP_TRIGGER,
} = HEADING;

const {
  ENTER_TOTAL_CROP,
  ENTER_RESIDUE_TYPE,
  ENTER_CROP_PERCENTAGE,
  SELECT_SOIL_TEXTURE,
  SELECT_SOIL_MOISTURE,
  SELECT_SOIL_PH,
  ENTER_SOIL_ORGANIC_MATTER,
  ENTER_SOIL_ORGANIC_CARBON,
} = PLACEHOLDER;

const { INPUT, SELECT } = FieldType;
const createOptions = (enumObject: object) =>
  Object.values(enumObject).map(value => ({ label: value, value }));

const cropResidueOptions = createOptions(CropResidueManagementType);
const soilTextureOptions = createOptions(SoilTexture);
const soilMoistureOptions = createOptions(SoilMoisture);
const soilPhOptions = createOptions(SoilPh);
const getSoilFieldName = (field: string) =>
  `${COMMODITY}.${SOIL_FIELD}.${field}`;

const getCommodityFieldName = (field: string) => `${COMMODITY}.${field}`;

export const GetCropResidueInitialField = (
  props: getCropResidueInitialFieldProps
) => {
  const { trigger, watch } = useFormContext();
  const cropResidues = watch(COMMODITY_CROP_TRIGGER);
  const { fetchData, isDisabled } = props;
  const handleTotalCropResidueChange = () => {
    const fieldNamesToTrigger = cropResidues.map((_: unknown, idx: number) => {
      return `commodity.cropResidues.${idx}.residueManagementType`;
    });
    trigger(fieldNamesToTrigger);
  };
  return {
    data: [
      {
        type: INPUT,
        fieldProps: {
          name: getCommodityFieldName(TOTAL_CROP_RESIDUE_FIELD),
          label: TOTAL_CROP,
          placeholderstring: ENTER_TOTAL_CROP,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_total_crop_residue',
            }),
          disabled: isDisabled,
          onChange: () => handleTotalCropResidueChange(),
        },
        span: 5,
      },
    ],
  };
};

export const GetCropResidueResidueManagementField = (
  props: cropResidueFieldProps
) => {
  const { watch, setValue, trigger } = useFormContext();
  const { remove, index, cropResidueLength, fetchData, isDisabled } = props;
  const cropResidues = watch(COMMODITY_CROP_TRIGGER);
  const removeHandler = () => {
    remove?.(index);
    trigger(COMMODITY_CROP_TRIGGER);
  };
  const getCropResidueField = (index: number, field: string) =>
    `${COMMODITY}.${CROP_RESIDUE_FIELD}.${index}.${field}`;

  const handleResidueChange = (index: number, value: string) => {
    setValue(`commodity.cropResidues.${index}.residueManagementType`, value, {
      shouldValidate: true,
    });
    trigger(`commodity.cropResidues.${index}.totalCropResiduePercentage`);
    const fieldNamesToTrigger = cropResidues.map((_: unknown, idx: number) => {
      return `commodity.cropResidues.${idx}.residueManagementType`;
    });
    trigger(fieldNamesToTrigger);
  };

  const handleCropPercentageChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.trim();
    // Convert value to number, default to undefined if empty
    const numericValue = value === '' ? undefined : Number(value);
    setValue(
      `commodity.cropResidues.${index}.totalCropResiduePercentage`,
      numericValue,
      {
        shouldValidate: true,
      }
    );
    trigger(`commodity.cropResidues.${index}.residueManagementType`);
    const fieldNamesToTrigger = cropResidues.map((_: unknown, idx: number) => {
      return `commodity.cropResidues.${idx}.totalCropResiduePercentage`;
    });
    trigger(fieldNamesToTrigger);
  };

  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: getCropResidueField(index, RESIDUE_TYPE_FIELD),
          label: RESIDUE_TYPE,
          options: cropResidueOptions,
          placeholderstring: ENTER_RESIDUE_TYPE,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_residue_management_type',
            }),
          disabled: isDisabled,
          onChange: (value: string) => handleResidueChange(index, value),
        },
        span: 5,
      },
      {
        type: INPUT,
        fieldProps: {
          name: getCropResidueField(index, TOTAL_CROP_PERCENTAGE_FIELD),
          label: TOTAL_CROP_PERCENTAGE,
          placeholderstring: ENTER_CROP_PERCENTAGE,
          onFocus: () =>
            fetchData({
              'input-field':
                'farmbox_ag_commodity_percentage_of_total_crop_residue',
            }),
          disabled: isDisabled,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            handleCropPercentageChange(index, event),
        },
        span: 5,
      },
      {
        type: FieldType.ELEMENT,
        additionalElement: cropResidueLength && cropResidueLength > 1 && (
          <div
            className={clsx(styles.deleteContainer, {
              [styles.hideAction]: isDisabled,
            })}
            onClick={removeHandler}>
            <DeleteButton />
          </div>
        ),
        span: 2,
      },
    ],
  };
};

export const getSoilMatterField = (props: getSoilMatterFieldProps) => {
  const { debouncedSoilCalculation, fetchData, isDisabled } = props;
  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: getSoilFieldName(SOIL_TEXTURE_FIELD),
          label: SOIL_TEXTURE,
          placeholderstring: SELECT_SOIL_TEXTURE,
          options: soilTextureOptions,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_soil_texture',
            }),
          disabled: isDisabled,
        },
        span: 5,
      },
      {
        type: SELECT,
        fieldProps: {
          name: getSoilFieldName(SOIL_MOISTURE_FIELD),
          label: SOIL_MOISTURE,
          placeholderstring: SELECT_SOIL_MOISTURE,
          options: soilMoistureOptions,
          onFocus: () =>
            fetchData({
              'input-field': 'farmbox_ag_commodity_soil_moisture',
            }),
          disabled: isDisabled,
        },
        span: 5,
      },
      {
        type: INPUT,
        fieldProps: {
          name: getSoilFieldName(SOIL_ORGANIC_PERCENTAGE_FIELD),
          label: SOIL_ORGANIC_PERCENTAGE,
          placeholderstring: ENTER_SOIL_ORGANIC_MATTER,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = parseFloat(e.target.value) || 0;
            debouncedSoilCalculation(value);
          },
          onFocus: () =>
            fetchData({
              'input-field':
                'farmbox_ag_commodity_soil_organic_matter_percentage',
            }),
          disabled: isDisabled,
        },
        span: 5,
      },
      {
        type: INPUT,
        fieldProps: {
          name: getSoilFieldName(SOIL_CARBON_PERCENTAGE_FIELD),
          label: SOIL_CARBON_PERCENTAGE,
          placeholderstring: ENTER_SOIL_ORGANIC_CARBON,
          onFocus: () =>
            fetchData({
              'input-field':
                'farmbox_ag_commodity_soil_organic_carbon_percentage',
            }),
          disabled: isDisabled,
        },
        span: 5,
      },
    ],
  };
};
